import React, { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { bookInterviewAndRedirect } from "../../utils/interviewBooking";
import "./notification.css";

const CalendarIcon = () => (
  <svg viewBox="0 0 24 24" className="medium-approval-icon">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 9h18M8 3v4M16 3v4" />
    <path d="M9 14l2 2 4-4" />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 24 24" className="medium-approval-icon">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8h.01M11 12h1v5h1" />
  </svg>
);

const VideoIcon = () => (
  <svg viewBox="0 0 24 24" className="medium-approval-icon">
    <rect x="3" y="5" width="14" height="14" rx="2" />
    <path d="M17 9l4-2v10l-4-2" />
  </svg>
);

const SupportIcon = () => (
  <svg viewBox="0 0 24 24" className="medium-approval-icon">
    <path d="M4 15v-3a8 8 0 0116 0v3" />
    <rect x="2" y="14" width="5" height="6" rx="1.5" />
    <rect x="17" y="14" width="5" height="6" rx="1.5" />
  </svg>
);

// interviewStatus and interviewAppointment both come from the
// `api/profile-info` response (see profile.js's getProfile(), which
// stores that response in its `profileSection` state):
//   interview_status: "Not Requested" -> real "Book 15 Minute Interview" button
//                     "Upcoming"      -> disabled "Upcoming" tile with the
//                                        real appointment date/time + link
//                     "Completed"     -> green "Interview Status: Completed" tile
//   interview_appointment: the appointment object (date, time, timezone,
//                     meeting_link, etc.) - only meaningful when status is
//                     "Upcoming", but this component doesn't assume that;
//                     it just checks whether the object/link exist.
// Defaults to "Not Requested"/null so nothing breaks if either field is
// ever missing (e.g. profileSection still loading on first render).
export default function Notifiacation({
  interviewStatus = "Not Requested",
  interviewAppointment = null,
}) {
  const { id } = useSelector((state) => state.user.user);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [showMoreInfo, setShowMoreInfo] = useState(false);

  const handleBookInterview = () => {
    if (bookingLoading) return;
    setBookingLoading(true);
    bookInterviewAndRedirect(id).catch((err) => {
      setBookingLoading(false);
      console.log(err);
      toast.error(
        err.message || "Unable to get your interview link right now."
      );
    });
  };

  return (
    <div className="medium-approval-wrap">
      <div className="medium-approval-notice">
        {interviewStatus === "Not Requested" ? (
          <>
            <div className="medium-approval-notice-header">
              <span className="medium-approval-notice-icon">
                <InfoIcon />
              </span>
              <div>
                <h3 className="medium-approval-notice-title">
                  Application Under Review
                </h3>
                <p className="medium-approval-notice-subtitle">
                  Your membership account is awaiting approval by the site
                  administrator.
                </p>
              </div>
            </div>

            <div className="medium-approval-notice-callout">
              <strong>Action needed:</strong> Please schedule your required
              15-minute interview below. Your application can't be reviewed
              for approval as a VerySoul practitioner until it's completed.
            </div>

            <div className="medium-approval-notice-secondary">
              <p>
                <strong>Already scheduled your interview?</strong> Check
                your email for your booking confirmation and Zoom link.
              </p>
              <p>
                While you wait, we recommend reviewing our{" "}
                <a
                  href="https://verysoul.com/medium-faqs"
                  target="_blank"
                  rel="noreferrer"
                >
                  Medium FAQs
                </a>{" "}
                and{" "}
                {/* TODO: swap in the real Medium Information URL */}
                <a href="#">Medium Information</a> to help you prepare for
                your interview.
              </p>
              <p>
                After your interview is complete and your application has
                been approved, you'll receive an email confirming that your
                practitioner account is active and ready to use.
              </p>
            </div>

            <p className="medium-approval-notice-support">
              <strong>Need help?</strong> If your application has been
              pending for more than 48 hours after your interview, please{" "}
              <a
                href={process.env.REACT_APP_SUPPORT_URL}
                target="_blank"
                rel="noreferrer"
              >
                Submit a Support Ticket
              </a>
              .
            </p>
          </>
        ) : (
          // Shown once the interview is scheduled or completed
          // (interviewStatus is "upcoming" or "completed") - same card,
          // different copy since there's no more booking action needed.
          <>
            <div className="medium-approval-notice-header">
              <span className="medium-approval-notice-icon">
                <InfoIcon />
              </span>
              <div>
                <h3 className="medium-approval-notice-title">
                  Your membership account is awaiting approval by the site
                  administrator.
                </h3>
              </div>
            </div>

            <div className="medium-approval-notice-secondary">
              <p>
                Your account is currently pending approval. The next step is
                to schedule your <strong>15-minute interview</strong>, which
                is required before your application can be reviewed for
                approval as a VerySoul practitioner.
              </p>
              <p>
                While you wait, we encourage you to review our{" "}
                <a
                  href="https://verysoul.com/medium-faqs"
                  target="_blank"
                  rel="noreferrer"
                >
                  Medium FAQs
                </a>{" "}
                and{" "}
                {/* TODO: swap in the real Medium Information URL */}
                <a href="#">Medium Information</a> to help you prepare for
                your interview.
              </p>
              <p>
                Once your interview has been completed and your application
                has been approved, you'll receive an email letting you know
                your practitioner account is active and ready to use.
              </p>
            </div>

            <p className="medium-approval-notice-support">
              If you need assistance, or if your application has been
              pending for more than 48 hours after your interview, please{" "}
              <a
                href={process.env.REACT_APP_SUPPORT_URL}
                target="_blank"
                rel="noreferrer"
              >
                Submit a Support Ticket
              </a>
              .
            </p>
          </>
        )}
      </div>

      <div className="medium-approval-actions">
        {interviewStatus === "Completed" ? (
          <button
            type="button"
            className="medium-approval-tile medium-approval-tile-completed"
            disabled
          >
            <span className="medium-approval-icon-circle">
              <CalendarIcon />
            </span>
            <span className="medium-approval-label">
              Interview Status: Completed
            </span>
          </button>
        ) : interviewStatus === "Upcoming" ? (
          // Plain <div>, not a <button>, because it holds a real nested
          // link ("Get Link Here") - a <button> can't legally contain
          // interactive content like an <a>, and a disabled ancestor
          // button would make that link unreliable to click across
          // browsers anyway.
          <div className="medium-approval-tile medium-approval-tile-disabled">
            <span className="medium-approval-icon-circle">
              <CalendarIcon />
            </span>
            <span className="medium-approval-label">Upcoming</span>
            <span className="medium-approval-tile-note">
              {interviewAppointment
                ? `${interviewAppointment.date} at ${interviewAppointment.time} (${interviewAppointment.timezone})`
                : "Appointment details unavailable"}
            </span>
            {interviewAppointment && interviewAppointment.meeting_link ? (
              <a
                href={interviewAppointment.meeting_link}
                target="_blank"
                rel="noreferrer"
                className="medium-approval-tile-link"
              >
                Get Link Here
              </a>
            ) : (
              // No real link to send them to yet - visual only, matching
              // the pattern used elsewhere in this file for placeholders.
              <a
                href="#"
                className="medium-approval-tile-link"
                onClick={(e) => e.preventDefault()}
              >
                Get Link Here
              </a>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="medium-approval-tile medium-approval-tile-featured"
            onClick={handleBookInterview}
            disabled={bookingLoading}
          >
            <span className="medium-approval-icon-circle">
              <CalendarIcon />
            </span>
            <span className="medium-approval-label">
              {bookingLoading ? "Loading..." : interviewStatus === "Canceled" || interviewStatus === "No Shows" ? "Book a New Interview" : "Book 15 Minute Interview"}
            </span>
          </button>
        )}

        <button
          type="button"
          className="medium-approval-tile"
          onClick={() => setShowMoreInfo((prev) => !prev)}
          aria-expanded={showMoreInfo}
          aria-controls="medium-approval-links"
        >
          <span className="medium-approval-icon-circle">
            <InfoIcon />
          </span>
          <span className="medium-approval-label">
            More Information About our Mediumship Program
          </span>
        </button>

        {/* Video not ready yet - swap href once it's published */}
        <a
          className="medium-approval-tile medium-approval-tile-disabled"
          href="#"
          onClick={(e) => e.preventDefault()}
        >
          <span className="medium-approval-icon-circle">
            <VideoIcon />
          </span>
          <span className="medium-approval-label">
            Watch our FAQ Video (coming soon)
          </span>
        </a>

        <a
          className="medium-approval-tile"
          href={process.env.REACT_APP_SUPPORT_URL}
          target="_blank"
          rel="noreferrer"
        >
          <span className="medium-approval-icon-circle">
            <SupportIcon />
          </span>
          <span className="medium-approval-label">Contact Support</span>
        </a>
      </div>

      <div
        id="medium-approval-links"
        className={`medium-approval-links${
          showMoreInfo ? " medium-approval-links-open" : ""
        }`}
      >
        <span>
          Review our{" "}
          <a
            href="https://verysoul.com/code-of-ethics/"
            target="_blank"
            rel="noreferrer"
          >
            Code of Ethics
          </a>
        </span>
        <span>
          Review our{" "}
          <a
            href="https://verysoul.com/operating-principle/"
            target="_blank"
            rel="noreferrer"
          >
            Operating Principles
          </a>
        </span>
        <span>
          Review our{" "}
          <a
            href="https://verysoul.com/medium-faqs"
            target="_blank"
            rel="noreferrer"
          >
            Medium FAQs
          </a>
        </span>
      </div>
    </div>
  );
}
